import { AppKitNetwork, bsc, mainnet, polygon, tron } from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { UAParser } from 'ua-parser-js';
import { http } from 'viem';
import { createStorage } from 'wagmi';

import { isDev } from '@/constants';

mainnet.custom = {
  icon: '/chains/ethereum.svg',
};

bsc.custom = {
  icon: '/chains/bsc.svg',
};

polygon.custom = {
  icon: '/chains/pol.svg',
};

tron.custom = {
  icon: '/chains/tron.svg',
};

export const DEVNET_L1A_CHAIN = {
  id: 1892,
  chainNamespace: 'eip155',
  caipNetworkId: 'eip155:1892',
  name: 'Devnet L1A',
  rpcUrls: { default: { http: ['https://geth-devnet-l1a.coinweb.io/'] } },
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  blockExplorers: { default: { name: 'DEVNET_L1A Explorer', url: 'https://explorer-devnet.coinweb.io' } },
  custom: {
    icon: '/chains/devnet_l1a.svg',
  },
  testnet: true,
} as const satisfies AppKitNetwork;

export const DEVNET_L1B_CHAIN = {
  id: 1893,
  chainNamespace: 'eip155',
  caipNetworkId: 'eip155:1893',
  name: 'Devnet L1B',
  rpcUrls: { default: { http: ['https://geth-devnet-l1b.coinweb.io'] } },
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  custom: {
    icon: '/chains/devnet_l1b.svg',
  },
  testnet: true,
} as const satisfies AppKitNetwork;

let REOWN_CONFIG: { chains: [AppKitNetwork, ...AppKitNetwork[]] };

// Select wallets to be shown in the modal's connection view with sorting priority.
// Selection is done by UserAgent
// Follow the list of ids: https://docs.reown.com/cloud/wallets/wallet-list
const REOWN_WALLET_ID_SELECTION: string[] = (() => {
  const userAgent = UAParser(window.navigator.userAgent);

  const defaultWalletSelection: string[] = [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // Metamask
    '2a87d74ae02e10bdd1f51f7ce6c4e1cc53cd5f2c0b6b5ad0d7b3007d2b13de7b', // XVerse Wallet
    '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709', // OKX
    // '19177a98252e07ddfc9af2083ba8e07ef627cb6103467ffebb3f8f4205fd7927', // LedgerLive
    // 'f5b4eeb6015d66be3f5940a895cbaa49ef3439e518cd771270e6b553b48f31d2', // MEW
    // '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
  ];

  // Android
  if (userAgent.device.is(UAParser.DEVICE.MOBILE) && userAgent.os.is('Android')) {
    // Add Android specific wallets here
    return defaultWalletSelection.concat([]);
  }

  // iOS
  if (userAgent.device.is(UAParser.DEVICE.MOBILE) && userAgent.os.is('iOS')) {
    // Add iOS specific wallets here
    return defaultWalletSelection.concat([]);
  }

  // Desktop
  if (userAgent.device.type === undefined) {
    // Add Desktop specific wallets here
    return defaultWalletSelection.concat([]);
  }

  return defaultWalletSelection;
})();

// Note, this checks the mode, not NODE_ENV, and in the default build
// we set mode to "devnet", so this build without setting --mode is
// when yarn build:production is run.
if (!isDev) {
  REOWN_CONFIG = {
    chains: [
      mainnet,
      bsc,
      polygon,
      tron,
      // bitcoin
    ],
  };
} else {
  REOWN_CONFIG = { chains: [DEVNET_L1B_CHAIN, DEVNET_L1A_CHAIN] };
}

const wagmiAdapter = new WagmiAdapter({
  storage: createStorage({ storage: window.sessionStorage }),
  networks: REOWN_CONFIG.chains,
  projectId: import.meta.env.VITE_APPKIT_PROJECT_ID,
  customRpcUrls: {
    'eip155:1892': [
      {
        url: 'https://geth-devnet-l1a.coinweb.io/',
      },
    ],
    'eip155:1893': [
      {
        url: 'https://geth-devnet-l1b.coinweb.io/',
      },
    ],
  },
  ...(isDev ? { transports: Object.fromEntries(REOWN_CONFIG.chains.map((chain) => [chain.id, http()])) } : {}),
});

export { REOWN_CONFIG, REOWN_WALLET_ID_SELECTION, wagmiAdapter };
